import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect } from 'react';

import { initPostHog } from 'lib/posthog/client';
import { setProvider } from 'lib/posthog/providers';
import posthogProvider from 'lib/posthog/providers/posthog';

/**
 * PostHogProvider initializes the PostHog client and tracks pageviews
 * on route changes. All flag/experiment access goes through the
 * module-level singleton via usePostHogExperiment — no React context.
 *
 * bootstrapFlags is accepted for future SSR flag seeding but is not
 * wired to getServerSideProps yet. The empty deps array is intentional:
 * bootstrapFlags comes from SSR props and is stable after hydration.
 */
const PostHogProvider = ({ bootstrapFlags, children }) => {
  const router = useRouter();

  useEffect(() => {
    let handleRouteChange;

    initPostHog().then((posthog) => {
      if (!posthog) return;

      setProvider(posthogProvider);

      if (bootstrapFlags) {
        posthog.featureFlags.override(bootstrapFlags);
      }

      posthog.capture('$pageview');

      handleRouteChange = () => posthog.capture('$pageview');
      router.events.on('routeChangeComplete', handleRouteChange);
    });

    return () => {
      if (handleRouteChange) {
        router.events.off('routeChangeComplete', handleRouteChange);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return children;
};

PostHogProvider.propTypes = {
  bootstrapFlags: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
  ),
  children: PropTypes.node.isRequired,
};

export default PostHogProvider;
